import { useTranslation } from "react-i18next";
import { DISTRICTS } from "../../../mocks/allVenues";

export type SortOption = "popular" | "rating" | "distance" | "newest";

export interface FilterState {
  districts: string[];
  sort: SortOption;
  openOnly: boolean;
  sponsoredOnly: boolean;
  editorOnly: boolean;
}

interface Props {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
  totalCount: number;
  filteredCount: number;
}

const SORT_OPTIONS: { value: SortOption; labelKey: string }[] = [
  { value: "popular", labelKey: "cat_page_sort_popular" },
  { value: "rating", labelKey: "cat_page_sort_rating" },
  { value: "distance", labelKey: "cat_page_sort_distance" },
  { value: "newest", labelKey: "cat_page_sort_newest" },
];

export default function FilterBar({
  filters,
  onChange,
  isMobileOpen,
  onMobileClose,
  filteredCount,
}: Props) {
  const { t } = useTranslation();

  const toggleDistrict = (d: string) => {
    const next = filters.districts.includes(d)
      ? filters.districts.filter((x) => x !== d)
      : [...filters.districts, d];
    onChange({ ...filters, districts: next });
  };

  const hasActiveFilters =
    filters.districts.length > 0 ||
    filters.openOnly ||
    filters.sponsoredOnly ||
    filters.editorOnly;

  const resetFilters = () =>
    onChange({ districts: [], sort: "popular", openOnly: false, sponsoredOnly: false, editorOnly: false });

  const content = (
    <div className="flex flex-col gap-6">
      {/* Result count */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-anthracite-400 font-body">
          <strong className="text-anthracite-800 font-semibold">{filteredCount}</strong> {t("cat_page_results")}
        </span>
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="text-xs text-forest-600 hover:text-forest-500 font-semibold font-body cursor-pointer whitespace-nowrap"
          >
            {t("cat_page_reset_filters")}
          </button>
        )}
      </div>

      {/* Sort */}
      <div>
        <h3 className="text-xs font-bold text-anthracite-500 uppercase tracking-wider font-body mb-3">
          {t("cat_page_filter_sort")}
        </h3>
        <div className="flex flex-col gap-1.5">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onChange({ ...filters, sort: opt.value })}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-body text-left transition-colors cursor-pointer ${
                filters.sort === opt.value
                  ? "bg-forest-50 text-forest-700 font-semibold"
                  : "text-anthracite-600 hover:bg-stone-50"
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                filters.sort === opt.value ? "border-forest-600 bg-forest-600" : "border-stone-300"
              }`}>
                {filters.sort === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
              </div>
              {t(opt.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* District */}
      <div>
        <h3 className="text-xs font-bold text-anthracite-500 uppercase tracking-wider font-body mb-3">
          {t("cat_page_filter_district")}
        </h3>
        <div className="flex flex-col gap-1.5">
          {DISTRICTS.map((district) => (
            <button
              key={district}
              onClick={() => toggleDistrict(district)}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-body text-left transition-colors cursor-pointer ${
                filters.districts.includes(district)
                  ? "bg-forest-50 text-forest-700 font-semibold"
                  : "text-anthracite-600 hover:bg-stone-50"
              }`}
            >
              <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                filters.districts.includes(district)
                  ? "border-forest-600 bg-forest-600"
                  : "border-stone-300"
              }`}>
                {filters.districts.includes(district) && (
                  <i className="ri-check-line text-white text-xs" />
                )}
              </div>
              {district}
            </button>
          ))}
        </div>
      </div>

      {/* Type toggles */}
      <div>
        <h3 className="text-xs font-bold text-anthracite-500 uppercase tracking-wider font-body mb-3">
          {t("cat_page_filter_type")}
        </h3>
        <div className="flex flex-col gap-2">
          {[
            { key: "openOnly", labelKey: "cat_page_filter_open", icon: "ri-time-line" },
            { key: "editorOnly", labelKey: "cat_page_filter_editor", icon: "ri-star-line" },
            { key: "sponsoredOnly", labelKey: "cat_page_filter_sponsored", icon: "ri-shield-star-line" },
          ].map((item) => (
            <button
              key={item.key}
              onClick={() =>
                onChange({ ...filters, [item.key]: !filters[item.key as keyof FilterState] })
              }
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-body text-left transition-colors cursor-pointer ${
                filters[item.key as keyof FilterState]
                  ? "bg-forest-50 text-forest-700 font-semibold"
                  : "text-anthracite-600 hover:bg-stone-50"
              }`}
            >
              <div className={`w-5 h-5 flex items-center justify-center rounded-lg transition-colors ${
                filters[item.key as keyof FilterState] ? "bg-forest-600" : "bg-stone-100"
              }`}>
                <i className={`${item.icon} text-xs ${
                  filters[item.key as keyof FilterState] ? "text-white" : "text-stone-500"
                }`} />
              </div>
              {t(item.labelKey)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-60 shrink-0">
        <div className="sticky top-24 bg-white border border-stone-100 rounded-2xl p-5">
          {content}
        </div>
      </aside>

      {/* Mobile drawer */}
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-anthracite-900/50" onClick={onMobileClose} />
          <div className="relative ml-auto w-72 bg-white h-full overflow-y-auto p-5 flex flex-col gap-4 animate-slide-in">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-bold text-anthracite-900 text-lg">
                {t("cat_page_filter_title")}
              </h2>
              <button onClick={onMobileClose} className="cursor-pointer p-1">
                <i className="ri-close-line text-xl text-anthracite-500" />
              </button>
            </div>
            {content}
          </div>
        </div>
      )}
    </>
  );
}
